import classnames from "classnames";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MediaQuery } from "@outplayed/responsive";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { Icon } from "@ugitgud/legos/ui/icon/Icon";
import { SvgUggPlusOverlay } from "@ugitgud/legos/ui/icon/SVG/UggPlusOverlay";
import { getRoleLabel, RoleS } from "@ugg/shared/utils/role-helpers";
import { buildQueryParams } from "@ugg/shared/utils/url-query-params";
import { numberWithCommas } from "@ugg/shared/utils/math";
import { getTierColor, getTierColorHex } from "@ugg/shared/utils/tier-helpers";
import { tierListSettings } from "@ugg/shared/components/TierList/lib/tier-list-settings";
import { RankingStatsInfo } from "@ugg/shared/api/data-parser/champions/ranking-stats";
import { getChampionBuildUrl } from "@ugg/shared/routes/app-routes";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { QueueTypeS, getQueueTypeName } from "@ugg/shared/utils/queue-type-helpers";
import { capitalizeMulti } from "@ugg/shared/utils/multi-build-helpers";
import { twMerge } from "tailwind-merge";
import { useUserPremiumState } from "@ugg/shared/api/requests/accounts/user-state";

export interface PersonalizedMatchup {
  championId: number;
  winRate: number;
  games: number;
}

interface PersonalizedMatchupsProps {
  matchups: PersonalizedMatchup[];
  summonerName: string;
  tagLine: string;
}

const PersonalizedMatchups = ({ matchups, summonerName, tagLine }: PersonalizedMatchupsProps) => {
  const { getChampionImg, getChampionName } = getRiotAssetsContext();

  return (
    <div className="flex flex-shrink-0 border-r border-accent-purple-700 pr-3">
      <div className="relative flex flex-col w-[220px] overflow-hidden rounded-[3px] p-1.5 gap-1.5 bg-purple-200">
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-[1]">
          <Icon icon={<SvgUggPlusOverlay />} className="w-[400%] -ml-[50%] h-auto min-h-full text-[#C8AC70] opacity-[0.15]" />
        </div>

        <div className="relative z-[1] flex items-baseline gap-[6px]">
          <span className="text-[14px] font-semibold text-accent-yellow-400 leading-none">{summonerName}</span>
          <span className="text-[12px] text-accent-gray-200 leading-none font-medium">{tagLine}</span>
          <span className="text-[14px] font-semibold text-white leading-none">vs.</span>
        </div>

        <div className="relative z-[1] flex gap-2">
          {matchups.map((matchup, index) => {
            const championImg = getChampionImg(matchup.championId, { webp: true });
            const championName = getChampionName(matchup.championId);

            return (
              <div
                key={`${index}_${matchup.championId}`}
                className="flex flex-1 flex-col items-center gap-1.5 p-1.5 rounded-[3px] bg-purple-400"
              >
                <img className="w-[26px] h-[26px] rounded-full" src={championImg} alt={championName} />
                <hr className="w-full border-t border-accent-purple-700 !m-0 !w-full" />
                <div className="flex flex-col items-center gap-[2px]">
                  <span
                    className="text-[10px] font-bold leading-tight whitespace-nowrap"
                    style={{ color: getTierColorHex(matchup.winRate) }}
                  >
                    {matchup.winRate}%
                  </span>
                  <span className="text-[10px] text-accent-gray-100 leading-tight whitespace-nowrap">{matchup.games} Games</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface PersonalizedMatchupsPlusCTAProps {
  utmSource?: string;
}

export const PersonalizedMatchupsPlusCTA = ({ utmSource = "personalizedmatchups" }: PersonalizedMatchupsPlusCTAProps) => {
  const { getChampionImg } = getRiotAssetsContext();
  const placeholderMatchups = [
    { championId: 222, winRate: 67, games: 6, color: getTierColorHex(45) }, // Jinx
    { championId: 30, winRate: 67, games: 12, color: "#3273FA" }, // Karthus
    { championId: 90, winRate: 67, games: 6, color: "#3273FA" }, // Malzahar
  ];

  return (
    <div className="relative flex flex-col flex-shrink-0 w-[220px] overflow-hidden rounded-[3px] p-1.5 gap-[10px] bg-purple-200">
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-[1]">
        <Icon icon={<SvgUggPlusOverlay />} className="w-[400%] -ml-[50%] h-auto min-h-full text-[#C8AC70] opacity-[0.15]" />
      </div>

      <div className="relative z-[1] flex items-center gap-[8px]">
        <a
          href={`/plus?utm_source=${utmSource}`}
          className="flex items-center justify-center px-[6px] py-[2px] rounded-[3px] border border-accent-yellow-400 text-[11px] font-bold text-accent-yellow-400 bg-accent-brown-400 leading-none"
        >
          PLUS
        </a>
        <span className="text-[14px] font-semibold text-white leading-tight">Personal Counters</span>
      </div>

      <div className="relative z-[1] flex gap-2">
        {placeholderMatchups.map((matchup, index) => (
          <div key={index} className="flex flex-1 flex-col items-center gap-1.5 p-1.5 rounded-[3px] bg-purple-400">
            <div className="relative w-[26px] h-[26px] rounded-full overflow-hidden select-none blur-[1px]" aria-hidden="true">
              <img className="w-full h-full" src={getChampionImg(matchup.championId, { webp: true })} />
              <div className="absolute inset-0 bg-purple-200/95 rounded-full" />
            </div>
            <hr className="w-full border-t border-accent-purple-700 !m-0 !w-full" />
            <div className="flex flex-col items-center gap-[2px]" aria-hidden="true">
              <span
                className="text-[10px] font-bold leading-tight whitespace-nowrap blur-[1.5px] select-none"
                style={{ color: matchup.color }}
              >
                {matchup.winRate}%
              </span>
              <span className="text-[10px] text-accent-gray-100 leading-tight whitespace-nowrap blur-[1.5px] select-none">
                {matchup.games} Games
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PersonalizedMatchupsInsufficientData = ({ queueType, role }: { queueType: string; role: string }) => {
  return (
    <div className="relative flex flex-col flex-shrink-0 w-[220px] overflow-hidden rounded-[3px] p-1.5 gap-[10px] bg-purple-200">
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-[1]">
        <Icon icon={<SvgUggPlusOverlay />} className="w-[400%] -ml-[50%] h-auto min-h-full text-[#C8AC70] opacity-[0.15]" />
      </div>

      <div className="relative z-[1] flex items-center gap-[8px]">
        <span className="flex items-center justify-center px-[6px] py-[2px] rounded-[3px] border border-accent-yellow-400 text-[11px] font-bold text-accent-yellow-400 bg-accent-brown-400 leading-none">
          PLUS
        </span>
        <span className="text-[14px] font-semibold text-white leading-tight">Personal Counters</span>
      </div>

      <div className="relative z-[1] flex items-center justify-center w-full h-[77px] rounded-[3px] bg-purple-100 px-3 text-center">
        <span className="text-[11px] text-accent-gray-100 leading-snug">
          Play at least{" "}
          <span className="font-bold text-white">
            5 games in {getQueueTypeName(queueType)} as {getRoleLabel(role, (label) => (label === "Bottom" ? "Bot" : label))}
          </span>{" "}
          on this champion to see your personal counters
        </span>
      </div>
    </div>
  );
};

interface MatchupsProps {
  matchups: RankingStatsInfo["counters"];
  matchupsCount: number;
  role: string | undefined;
}

const Matchups = (props: MatchupsProps) => {
  const { getChampionName, getNormalizedChampionName, getChampionImg } = getRiotAssetsContext();
  const { t } = useTranslation();
  const queryString = useQueryString();
  const { matchups, matchupsCount, role } = props;

  const matchupsToDisplay =
    matchups.length < matchupsCount
      ? matchups.slice(0, matchupsCount).concat(Array(matchupsCount - matchups.length).fill(null))
      : matchups;

  return (
    <>
      {matchupsToDisplay.slice(0, matchupsCount).map((champion, index) => {
        if (champion === null) {
          return null;
        }

        const { champion_id, matches, win_rate } = champion;

        const championName = getChampionName(champion_id);
        const championNormalizedName = getNormalizedChampionName(champion_id);
        const championImg = getChampionImg(champion_id, { webp: true });
        const winRate = Math.round((100 - win_rate) * 10) / 10;

        return (
          <Link
            key={`${index}_${champion_id}`}
            className="champion-matchup"
            to={{
              pathname: getChampionBuildUrl(championNormalizedName, { role }),
              search: buildQueryParams(queryString),
              state: { scrollTop: true },
            }}
          >
            <div className="champion-face">
              <img src={championImg} alt={championName} />
            </div>
            <div className="champion-name">{championName}</div>
            <hr />
            <div className="matchup-stats">
              <div className={`win-rate ${getTierColor(winRate)}`}>
                <strong>{winRate}%</strong>
              </div>
              <div className="total-matches">
                {numberWithCommas(matches)} {t("Matches")}
              </div>
            </div>
          </Link>
        );
      })}
    </>
  );
};

const preventDefault = (e: Event) => {
  e = e || window.event;
  if (e.preventDefault) {
    e.preventDefault();
  }
};
const disableScroll = () => {
  document?.addEventListener("wheel", preventDefault, {
    passive: false,
  });
};
const enableScroll = () => {
  document?.removeEventListener("wheel", preventDefault, false);
};
const onWheel = (e: React.WheelEvent) => {
  const container = document?.getElementById("toughest-matchups");
  const containerScrollPosition = document?.getElementById("toughest-matchups")?.scrollLeft;
  if (container !== null && containerScrollPosition !== undefined) {
    container?.scrollTo({
      top: 0,
      left: containerScrollPosition + e.deltaY + e.deltaX,
      behavior: "smooth",
    });
  }
};

interface ToughestMatchupsProps {
  className?: string;
  championId: number;
  matchups: RankingStatsInfo["counters"];
  matchupsCount?: number;
  role?: string;
  multi?: string;
  queueType: QueueTypeS;
  hideBlurb?: boolean;
  disableWheelScroll?: boolean;
  personalizedMatchups?: PersonalizedMatchup[];
  summonerName?: string;
  tagLine?: string;
  isUggPlus?: boolean;
}

export default function ToughestMatchups(props: ToughestMatchupsProps) {
  const {
    className,
    championId,
    matchups,
    matchupsCount = 10,
    role,
    multi,
    queueType,
    hideBlurb,
    disableWheelScroll,
    personalizedMatchups,
    summonerName,
    tagLine,
  } = props;
  const { data: premiumData } = useUserPremiumState();
  const isUggPlus = premiumData?.isPremium;
  const { t } = useTranslation();
  const { getChampionName } = getRiotAssetsContext();

  if (!tierListSettings[queueType]?.showAgainst) {
    return null;
  }

  const championName = getChampionName(championId);

  return (
    <div className={classnames(twMerge("content-section toughest-matchups", className))}>
      <div className="content-section_header">
        <span style={{ marginRight: 12 }}>{t("Toughest Matchups")}</span>
        {!hideBlurb && (
          <MediaQuery min="TABLET" max="DESKTOP_LARGE">
            {multi ? (
              <span className="content-section_blurb">
                {t(`These champions counter ${capitalizeMulti(multi)} ${championName}`, {
                  Champion: championName,
                  Role: getRoleLabel(role || ""),
                  multi: multi,
                })}
              </span>
            ) : (
              <span className="content-section_blurb">
                {t(`These champions counter ${championName}`, { Champion: championName, Role: getRoleLabel(role || "") })}
              </span>
            )}
          </MediaQuery>
        )}
      </div>
      <div
        id="toughest-matchups"
        className="matchups"
        onMouseEnter={!disableWheelScroll ? disableScroll : () => {}}
        onMouseLeave={!disableWheelScroll ? enableScroll : () => {}}
        onWheel={!disableWheelScroll ? onWheel : () => {}}
      >
        <div className="flex gap-3">
          {isUggPlus ? (
            personalizedMatchups && personalizedMatchups.length > 0 && summonerName && tagLine ? (
              <PersonalizedMatchups matchups={personalizedMatchups} summonerName={summonerName} tagLine={tagLine} />
            ) : (
              <PersonalizedMatchupsInsufficientData queueType={queueType} role={role || ""} />
            )
          ) : (
            <PersonalizedMatchupsPlusCTA utmSource="toughestmatchups" />
          )}
          <Matchups matchups={matchups} matchupsCount={matchupsCount} role={role} />
        </div>
      </div>
    </div>
  );
}
